import { h } from "../shared/safe";
import { errorBanner } from "../shared/blocks";
import { noticeBlock, section } from "./bits";
import type { Ctx } from "./context";
import { PAGE_TYPE_ORDER, type PageType } from "./model";

// Per-client page template choice (clients.template_selection_json, migration 0004). Saving on change is preserved:
// there is still no separate Save button. What is new: pickers are disabled while a save runs (two quick changes can
// no longer race and overwrite each other), a failed save snaps the control back to what is actually stored, a template
// the list no longer contains is shown as such instead of silently dropped, and pickers are not offered at all when the
// template list itself could not be loaded (saving then would erase the client's selection).

const PAGE_TYPE_LABELS: Record<PageType, string> = {
  landing: "Homepage",
  blog: "Blog post",
  service: "Service page (one per service)",
  location: "Location page (one per area, with map)",
  about: "About page",
  contact: "Contact page",
};
const REQUIRED_PAGE_TYPES = new Set<PageType>(["landing", "blog"]);

export function renderTemplates(ctx: Ctx): HTMLElement {
  const { data, state, actions } = ctx;
  const intro = h(
    "p",
    { class: "muted qf-cd-intro" },
    "Homepage and Blog fall back to whatever's newest if left on default — every existing client keeps working exactly as before. Service/Location/About/Contact pages are brand new and only get built when a template is picked here. Changes take effect on the next Deploy, not automatically.",
  );
  if (data.templates === null) {
    return section("cd-templates", "Page Templates", intro, errorBanner(`Templates could not be loaded${data.failures.templates ? `: ${data.failures.templates.message}` : ""}. Nothing is changed until they load.`, { retry: () => void actions.reloadSection("templates") }));
  }
  const busy = state.pending.has("template:save");
  const pickers = PAGE_TYPE_ORDER.map((pageType) => {
    const options = (data.templates as NonNullable<typeof data.templates>).filter((t) => t.pageType === pageType);
    const stored = data.client.templateSelection[pageType] ?? "";
    const known = stored === "" || options.some((t) => t.id === stored);
    const id = `cd-template-${pageType}`;
    const select = h(
      "select",
      {
        id,
        "aria-describedby": "cd-template-status",
        "aria-busy": busy ? "true" : false,
        "aria-disabled": busy ? "true" : false,
        dataset: { focus: `template:${pageType}`, pageType },
        on: {
          change: (ev) => {
            const el = ev.target as HTMLSelectElement;
            if (busy || state.pending.has("template:save")) {
              el.value = stored;
              return;
            }
            void actions.saveTemplate(pageType, el.value);
          },
        },
      },
      h("option", { value: "" }, REQUIRED_PAGE_TYPES.has(pageType) ? "Use default (newest)" : "Don't generate this page"),
      ...options.map((t) => h("option", { value: t.id }, t.name)),
      known ? null : h("option", { value: stored }, `Unavailable template (${stored.slice(0, 24)})`),
    );
    select.value = stored;
    return h("div", null, h("label", { for: id }, PAGE_TYPE_LABELS[pageType]), select);
  });
  return section(
    "cd-templates",
    "Page Templates",
    intro,
    data.warnings.some((w) => w.startsWith("Template selection"))
      ? h("p", { class: "muted" }, "The stored selection is unreadable, so every picker shows its default. Choosing a template replaces the stored value.")
      : null,
    h("div", { class: "grid cols-2", id: "cd-template-pickers" }, ...pickers),
    h("div", { id: "cd-template-status", class: "qf-cd-status", "aria-live": "polite" }, busy ? "Saving…" : noticeBlock(state.templateNotice)),
  );
}
